"use client";

import { useCallback, useEffect, useMemo, useState } from "react";

import log from "logbeacon";
import { usePathname } from "@hooks/usePathname";
import {
  DEFAULT_GUIDE_MODAL_CONFIG,
  GuideModalConfig,
  dismissGuideModal,
  shouldShowGuideModal,
} from "@/src/utils/guideModalStorage";

export interface UseGuideModalOptions extends Partial<GuideModalConfig> {
  /** 是否启用Hook */
  enabled?: boolean;
}

export interface UseGuideModalReturn {
  /** 是否应该显示弹窗 */
  shouldShow: boolean;
  /** 关闭弹窗的方法 */
  dismissModal: () => void;
  /** 当前配置 */
  config: GuideModalConfig;
  /** 是否正在加载 */
  isLoading: boolean;
}

/**
 * 通用弹窗状态管理Hook
 *
 * 提供弹窗的显示逻辑和控制方法，支持多种弹窗类型
 * 集成登录状态、时间控制、会话检测等功能
 *
 * @example
 * ```tsx
 * // 登录引导弹窗
 * const { shouldShow, dismissModal } = useGuideModal({
 *   modalType: GuideModalType.LOGIN_GUIDE,
 *   dismissDuration: 30,
 *   targetPages: ['/radar']
 * });
 *
 * // Agent引导弹窗
 * const { shouldShow, dismissModal } = useGuideModal({
 *   modalType: GuideModalType.AGENT_GUIDE,
 *   dismissDuration: 60,
 *   targetPages: ['/dashboard']
 * });
 *
 * return (
 *   <>
 *     {shouldShow && (
 *       <GuideModal onClose={dismissModal}>
 *         <GuideContent />
 *       </GuideModal>
 *     )}
 *   </>
 * );
 * ```
 *
 * @param options 配置选项
 * @returns Hook返回值
 */
export const useGuideModal = (
  options: UseGuideModalOptions = {},
): UseGuideModalReturn => {
  const pathname = usePathname(true);

  // 使用 useMemo 缓存配置对象，避免不必要的重新创建
  const config: GuideModalConfig = useMemo(
    () => ({
      ...DEFAULT_GUIDE_MODAL_CONFIG,
      ...options,
    }),
    [
      options.modalType,
      options.dismissDuration,
      options.targetPages,
      options.enableTimeControl,
      options.enabled,
      options.excludedPages,
    ],
  );

  const [shouldShow, setShouldShow] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // 检查是否应该显示弹窗
  const checkShouldShow = useCallback(() => {
    try {
      // 如果明确禁用，不显示
      if (options.enabled === false) {
        return false;
      }

      const result = shouldShowGuideModal(config, pathname);
      return result;
    } catch (error) {
      log.error("[GuideModal] Error in checkShouldShow:", error);
      return false;
    }
  }, [
    options.modalType,
    options.dismissDuration,
    options.targetPages,
    options.enableTimeControl,
    options.enabled,
    options.excludedPages,
    pathname,
  ]);

  // 关闭弹窗的方法
  const dismissModal = useCallback(() => {
    try {
      dismissGuideModal(config.modalType, config.dismissDuration);
      setShouldShow(false);
    } catch (error) {
      log.error("[GuideModal] Error in dismissModal:", error);
      // 即使出错也要关闭弹窗
      setShouldShow(false);
    }
  }, [config.modalType, config.dismissDuration]);

  // 直接在 useEffect 中处理逻辑，避免函数依赖链
  useEffect(() => {
    const newShouldShow = checkShouldShow();
    // log.debug(
    //   "[GuideModal] useEffect set shouldShow:",
    //   newShouldShow,
    //   pathname,
    // );
    setShouldShow(newShouldShow);
    setIsLoading(false);
  }, [
    options.modalType,
    options.dismissDuration,
    options.targetPages,
    options.enableTimeControl,
    options.enabled,
    options.excludedPages,
    pathname,
    checkShouldShow,
  ]);

  const returnValue: UseGuideModalReturn = {
    shouldShow,
    dismissModal,
    config,
    isLoading,
  };

  return returnValue;
};
